'use client'

import { createContext, useContext, useEffect, useMemo } from 'react'
import { useRouter } from 'next/navigation'

import { LOCALE_STORAGE_KEY, LOCALE_META, isLocale, type Locale } from '@/lib/i18n/config'
import { readLocaleCookie, writeLocaleCookie } from '@/lib/i18n/cookie'
import { createTranslator, type Messages, type TranslateValues, type Translator } from '@/lib/i18n/translate'

interface LocaleContextValue {
  locale: Locale
  messages: Messages
  t: Translator
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

/**
 * Carries the locale the server already resolved down to Client Components.
 *
 * Only the active catalog is passed in, so a browser downloads one language,
 * not five. The server is the single decision point — this component never
 * re-decides which language to show, it only reports it — which is what keeps
 * the first paint and the hydrated tree in agreement.
 */
export function LocaleProvider({
  locale,
  messages,
  children,
}: {
  locale: Locale
  messages: Messages
  children: React.ReactNode
}) {
  const router = useRouter()

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, messages, t: createTranslator(messages) }),
    [locale, messages],
  )

  useEffect(() => {
    // Keep localStorage as a mirror of the cookie. It is not what the server
    // reads — it cannot be, the server never sees it — but it survives things
    // the cookie does not: a cookie banner sweeping non-essential cookies, an
    // expiry, a browser clearing site data on close.
    try {
      const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY)

      if(!readLocaleCookie() && isLocale(stored) && stored !== locale) {
        // The cookie is gone but we know what they chose. Put it back and let
        // the server render the right language. This cannot loop: the next
        // render reads the restored cookie, so `locale` will equal `stored`.
        writeLocaleCookie(stored)
        router.refresh()
        return
      }

      if(stored !== locale) window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
    } catch {
      // Private mode, or storage disabled. The cookie alone is enough.
    }
  }, [locale, router])

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

function useLocaleContext(): LocaleContextValue {
  const context = useContext(LocaleContext)
  if(!context) throw new Error('useT / useLocale must be used inside a LocaleProvider')
  return context
}

/** The active locale and its display metadata. */
export function useLocale() {
  const { locale } = useLocaleContext()
  return { locale, ...LOCALE_META[locale] }
}

/**
 * The `t` function for a Client Component.
 *
 *     const t = useT()                 // t('auth.login.title')
 *     const t = useT('auth.login')     // t('title')
 *
 * The unscoped form keeps full key checking. The scoped form trades it away for
 * brevity, since a prefix plus a suffix cannot be validated as one literal.
 */
export function useT(): Translator
export function useT(prefix: string): (key: string, values?: TranslateValues) => string
export function useT(prefix?: string) {
  const { t } = useLocaleContext()
  return useMemo(() => (prefix ? t.scoped(prefix) : t), [t, prefix])
}
